<template>
  <header class="app-shell-header" :class="{ 'dark-mode': isDarkMode }">
    <a class="skip-link" href="#main-content">Asosiy kontentga o'tish</a>

    <div class="top-app-bar">
      <router-link
        to="/"
        class="brand-link"
        aria-label="NimaDegani bosh sahifa"
      >
        <img :src="logo" alt="" class="brand-logo" />
      </router-link>

      <nav class="navigation-rail" aria-label="Asosiy navigatsiya">
        <router-link
          v-for="item in navItems"
          :key="item.path"
          :to="item.path"
          class="nav-link"
          active-class="active"
          :aria-label="item.label"
        >
          <span class="nav-icon-wrap" aria-hidden="true">
            <component :is="item.icon" class="nav-icon" :size="20" :stroke-width="2" />
          </span>
          <span class="nav-label">{{ item.label }}</span>
        </router-link>
      </nav>

      <div class="app-actions">
        <SearchBar class="app-search" />
        <button
          type="button"
          class="icon-button"
          @click="toggleTheme"
          :title="themeLabel"
          :aria-label="themeLabel"
        >
          <Sun v-if="isDarkMode" :size="22" :stroke-width="2" aria-hidden="true" />
          <Moon v-else :size="22" :stroke-width="2" aria-hidden="true" />
        </button>
      </div>
    </div>

    <nav class="bottom-navigation" aria-label="Mobil navigatsiya">
      <router-link
        v-for="item in navItems"
        :key="`mobile-${item.path}`"
        :to="item.path"
        class="bottom-nav-item"
        active-class="active"
        :aria-label="item.label"
      >
        <span class="bottom-nav-indicator" aria-hidden="true">
          <component :is="item.icon" class="bottom-nav-icon" :size="22" :stroke-width="2" />
        </span>
        <span class="bottom-nav-label">{{ item.shortLabel || item.label }}</span>
      </router-link>
    </nav>
  </header>
</template>

<script>
import logo from '@/assets/NimaDegani.png';
import SearchBar from './SearchBar.vue';
import {
  BarChart3,
  FileText,
  Home,
  Info,
  Moon,
  PlusCircle,
  Sun,
  User,
} from 'lucide-vue-next';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'AppNavbar',
  components: {
    SearchBar,
    Moon,
    Sun,
  },
  data() {
    return {
      logo,
      navItems: [
        { path: '/', label: 'Bosh sahifa', shortLabel: 'Bosh', icon: Home },
        { path: '/submit', label: "So'z qo'shish", shortLabel: "Qo'shish", icon: PlusCircle },
        { path: '/about', label: 'Biz haqimizda', shortLabel: 'Biz', icon: Info },
        { path: '/profile', label: 'Profil', shortLabel: 'Profil', icon: User },
        { path: '/ranking', label: 'Reyting', shortLabel: 'Reyting', icon: BarChart3 },
        { path: '/blog', label: 'Blog', shortLabel: 'Blog', icon: FileText },
      ],
    };
  },
  computed: {
    ...mapGetters(['isDarkMode']),
    themeLabel() {
      return this.isDarkMode ? "Yorug' rejimga o'tish" : "Qorong'i rejimga o'tish";
    },
  },
  methods: {
    ...mapActions(['toggleTheme']),
  },
};
</script>

<style scoped>
.app-shell-header {
  position: sticky;
  top: 0;
  z-index: 1000;
  color: var(--md-sys-color-on-surface);
  background: var(--md-sys-color-surface);
  border-bottom: 1px solid var(--md-sys-color-outline-variant);
  box-shadow: var(--md-sys-elevation-1);
}

.skip-link {
  position: fixed;
  top: 8px;
  left: 8px;
  z-index: 1200;
  min-height: 48px;
  padding: 12px 18px;
  border-radius: var(--md-sys-shape-corner-full);
  color: var(--md-sys-color-on-primary);
  background: var(--md-sys-color-primary);
  box-shadow: var(--md-sys-elevation-3);
  font: var(--md-sys-typescale-label-large);
  text-decoration: none;
  transform: translateY(calc(-100% - 16px));
  transition: transform var(--md-sys-motion-standard);
}

.skip-link:focus-visible {
  transform: translateY(0);
}

.top-app-bar {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 20px;
  width: min(100% - calc(var(--app-page-padding) * 2), var(--app-content-max-width));
  min-height: var(--app-bar-height);
  margin: 0 auto;
  padding: 8px 0;
}

.brand-link {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 64px;
  min-height: 48px;
  overflow: hidden;
  border-radius: var(--md-sys-shape-corner-large);
}

.brand-link::before,
.nav-link::before,
.icon-button::before,
.bottom-nav-item::before {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: currentColor;
  content: "";
  opacity: 0;
  transition: opacity var(--md-sys-motion-short);
}

.brand-link:hover::before,
.nav-link:hover::before,
.icon-button:hover::before,
.bottom-nav-item:hover::before {
  opacity: var(--md-sys-state-hover-opacity);
}

.brand-link:active::before,
.nav-link:active::before,
.icon-button:active::before,
.bottom-nav-item:active::before {
  opacity: var(--md-sys-state-pressed-opacity);
}

.brand-logo {
  position: relative;
  display: block;
  width: auto;
  height: 44px;
  object-fit: contain;
}

.navigation-rail {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  overflow-x: auto;
  scrollbar-width: none;
}

.navigation-rail::-webkit-scrollbar {
  display: none;
}

.nav-link {
  position: relative;
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 48px;
  padding: 0 14px;
  overflow: hidden;
  border-radius: var(--md-sys-shape-corner-full);
  color: var(--md-sys-color-on-surface-variant);
  font: var(--md-sys-typescale-label-large);
  text-decoration: none;
  white-space: nowrap;
  transition:
    background-color var(--md-sys-motion-standard),
    color var(--md-sys-motion-standard);
}

.nav-link.active {
  color: var(--md-sys-color-on-secondary-container);
  background: var(--md-sys-color-secondary-container);
}

.nav-icon-wrap,
.nav-icon,
.bottom-nav-icon {
  position: relative;
  flex: 0 0 auto;
}

.app-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  min-width: 0;
}

.app-search {
  width: min(34vw, 380px);
}

.icon-button {
  position: relative;
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  overflow: hidden;
  border: 0;
  border-radius: var(--md-sys-shape-corner-full);
  color: var(--md-sys-color-on-surface-variant);
  background: transparent;
  cursor: pointer;
  transition:
    background-color var(--md-sys-motion-standard),
    color var(--md-sys-motion-standard);
}

.icon-button:hover,
.icon-button:focus-visible {
  color: var(--md-sys-color-primary);
}

.icon-button svg {
  position: relative;
}

.bottom-navigation {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1000;
  display: none;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  min-height: var(--app-bottom-nav-height);
  padding: 6px max(4px, env(safe-area-inset-left)) calc(6px + env(safe-area-inset-bottom)) max(4px, env(safe-area-inset-right));
  color: var(--md-sys-color-on-surface-variant);
  background: var(--md-sys-color-surface-container);
  border-top: 1px solid var(--md-sys-color-outline-variant);
  box-shadow: var(--md-sys-elevation-2);
}

.bottom-nav-item {
  position: relative;
  display: inline-flex;
  min-width: 0;
  min-height: 64px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  overflow: hidden;
  border-radius: var(--md-sys-shape-corner-large);
  color: inherit;
  font: var(--md-sys-typescale-label-medium);
  text-align: center;
  text-decoration: none;
}

.bottom-nav-indicator {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 32px;
  border-radius: var(--md-sys-shape-corner-full);
  transition: background-color var(--md-sys-motion-standard);
}

.bottom-nav-label {
  position: relative;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bottom-nav-item.active {
  color: var(--md-sys-color-on-secondary-container);
}

.bottom-nav-item.active .bottom-nav-indicator {
  background: var(--md-sys-color-secondary-container);
}

@media (max-width: 1120px) {
  .top-app-bar {
    grid-template-columns: auto minmax(0, 1fr) auto;
    gap: 12px;
  }

  .nav-label {
    display: none;
  }

  .nav-link {
    width: 48px;
    padding: 0;
  }
}

@media (max-width: 820px) {
  .app-shell-header {
    box-shadow: var(--md-sys-elevation-1);
  }

  .top-app-bar {
    grid-template-columns: auto minmax(0, 1fr) auto;
    min-height: 64px;
  }

  .navigation-rail {
    display: none;
  }

  .brand-logo {
    height: 38px;
  }

  .app-search {
    width: 100%;
  }

  .bottom-navigation {
    display: grid;
  }
}

@media (max-width: 420px) {
  .top-app-bar {
    gap: 8px;
  }

  .brand-link {
    min-width: 52px;
  }

  .brand-logo {
    max-width: 92px;
  }

  .bottom-nav-label {
    font-size: 0.6875rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .skip-link,
  .brand-link::before,
  .nav-link::before,
  .icon-button::before,
  .bottom-nav-item::before,
  .bottom-nav-indicator {
    transition: none;
  }
}

@media (prefers-contrast: high) {
  .app-shell-header,
  .bottom-navigation {
    border-color: var(--md-sys-color-outline);
  }

  .nav-link.active,
  .bottom-nav-item.active .bottom-nav-indicator {
    outline: 1px solid currentColor;
  }
}
</style>
